/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AnimaticPrimitiveConfig,
  AnimaticPrimitiveConfig$inboundSchema,
  AnimaticPrimitiveConfig$Outbound,
  AnimaticPrimitiveConfig$outboundSchema,
} from "./animaticprimitiveconfig.js";
import {
  ConditionExpression,
  ConditionExpression$inboundSchema,
  ConditionExpression$Outbound,
  ConditionExpression$outboundSchema,
} from "./conditionexpression.js";

/**
 * Defines a specific animatic state which is conditionally applied based on the condition.
 */
export type AnimaticPrimitiveState = {
  /**
   * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
   */
  conditionExpression?: ConditionExpression | undefined;
  /**
   * The configuration of the animatic primitive.
   */
  config: AnimaticPrimitiveConfig;
  /**
   * Whether this state is the default state. If this is true, conditions are not required. For readability, these states should be at the end of the array.
   */
  isDefault?: boolean | undefined;
  /**
   * The priority of the state. There is no constraint on overlapping conditions, so this field allows user to set priority.
   */
  priority?: number | undefined;
  /**
   * The name of the state
   */
  stateName: string;
};

/** @internal */
export const AnimaticPrimitiveState$inboundSchema: z.ZodType<
  AnimaticPrimitiveState,
  z.ZodTypeDef,
  unknown
> = z.object({
  condition_expression: types.optional(ConditionExpression$inboundSchema),
  config: AnimaticPrimitiveConfig$inboundSchema,
  is_default: types.boolean().default(false),
  priority: types.number().default(0),
  state_name: types.string(),
}).transform((v) => {
  return remap$(v, {
    "condition_expression": "conditionExpression",
    "is_default": "isDefault",
    "state_name": "stateName",
  });
});
/** @internal */
export type AnimaticPrimitiveState$Outbound = {
  condition_expression?: ConditionExpression$Outbound | undefined;
  config: AnimaticPrimitiveConfig$Outbound;
  is_default: boolean;
  priority: number;
  state_name: string;
};

/** @internal */
export const AnimaticPrimitiveState$outboundSchema: z.ZodType<
  AnimaticPrimitiveState$Outbound,
  z.ZodTypeDef,
  AnimaticPrimitiveState
> = z.object({
  conditionExpression: ConditionExpression$outboundSchema.optional(),
  config: AnimaticPrimitiveConfig$outboundSchema,
  isDefault: z.boolean().default(false),
  priority: z.number().int().default(0),
  stateName: z.string(),
}).transform((v) => {
  return remap$(v, {
    conditionExpression: "condition_expression",
    isDefault: "is_default",
    stateName: "state_name",
  });
});

export function animaticPrimitiveStateToJSON(
  animaticPrimitiveState: AnimaticPrimitiveState,
): string {
  return JSON.stringify(
    AnimaticPrimitiveState$outboundSchema.parse(animaticPrimitiveState),
  );
}
export function animaticPrimitiveStateFromJSON(
  jsonString: string,
): SafeParseResult<AnimaticPrimitiveState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AnimaticPrimitiveState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AnimaticPrimitiveState' from JSON`,
  );
}
